import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Button,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import {
  Home as HomeIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Info as InfoIcon,
  ContactMail as ContactIcon,
  Description as NewsIcon,
  People as PeopleIcon,
  EmojiEvents as SuccessIcon,
  Business as BusinessIcon,
  MenuBook as ProgramIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useSEO } from '../hooks/useSEO';
import WeChatQRDialog from '../components/common/WeChatQRDialog';
import useWeChatQR from '../hooks/useWeChatQR';

const Sitemap = () => {
  const theme = useTheme();
  const { openQR, openWeChatQR, closeWeChatQR } = useWeChatQR();
  const [dialogTitle, setDialogTitle] = useState('免费咨询');
  
  // 设置页面 SEO
  useSEO('sitemap');

  const siteMapData = [
    {
      title: '首页',
      icon: <HomeIcon />,
      description: '希航教育官网首页',
      links: [
        { text: '首页', href: '/', description: '了解希航教育，开启留学之旅' },
        { text: '热门推荐院校', href: '/#featured-universities', description: '精选马来西亚顶尖大学' },
        { text: '专业留学服务', href: '/#services', description: '全方位的留学支持服务' },
        { text: '为什么选择我们', href: '/#why-choose-us', description: '我们的核心优势' },
        { text: '成功案例', href: '/#testimonials', description: '学生真实反馈' },
        { text: '最新资讯', href: '/#news', description: '留学资讯和申请指南' },
        { text: '联系我们', href: '/#contact', description: '获取专业咨询' }
      ]
    },
    {
      title: '院校库',
      icon: <SchoolIcon />,
      description: '马来西亚大学详细信息',
      links: [
        { text: '所有院校', href: '/universities', description: '浏览所有合作院校' },
        { text: '马来亚大学', href: '/universities/university-of-malaya', description: 'QS世界排名 #70' },
        { text: '博特拉大学', href: '/universities/universiti-putra-malaysia', description: 'QS世界排名 #143' },
        { text: '马来西亚国民大学', href: '/universities/the-national-university-of-malaysia', description: 'QS世界排名 #129' },
        { text: '马来西亚理科大学', href: '/universities/universiti-sains-malaysia', description: 'QS世界排名 #137' },
        { text: '马来西亚工艺大学', href: '/universities/universiti-teknologi-malaysia', description: 'QS世界排名 #188' }
      ]
    },
    {
      title: '专业库',
      icon: <ProgramIcon />,
      description: '热门专业和课程信息',
      links: [
        { text: '所有专业', href: '/programs', description: '浏览所有专业课程' },
        { text: '商科专业', href: '/programs?category=business', description: '工商管理、金融、会计等' },
        { text: '工程专业', href: '/programs?category=engineering', description: '计算机、电子、机械工程等' },
        { text: '医学专业', href: '/programs?category=medicine', description: '临床医学、药学、护理等' },
        { text: '文科专业', href: '/programs?category=arts', description: '教育、语言、社会科学等' },
        { text: '理科专业', href: '/programs?category=science', description: '生物、化学、物理等' }
      ]
    },
    {
      title: '服务项目',
      icon: <AssignmentIcon />,
      description: '专业留学服务',
      links: [
        { text: '所有服务', href: '/services', description: '查看所有留学服务' },
        { text: '院校申请', href: '/services#university-application', description: '专业院校选择指导' },
        { text: '签证服务', href: '/services#visa-service', description: '全程签证申请指导' },
        { text: '语言培训', href: '/services#language-training', description: '雅思、托福培训' },
        { text: '住宿安排', href: '/services#accommodation', description: '多样化住宿选择' },
        { text: '接机服务', href: '/services#airport-pickup', description: '贴心接机服务' },
        { text: '全程支持', href: '/services#full-support', description: '留学期间持续支持' }
      ]
    },
    {
      title: '关于我们',
      icon: <InfoIcon />,
      description: '希航教育公司信息',
      links: [
        { text: '公司介绍', href: '/about', description: '了解希航教育发展历程' },
        { text: '团队介绍', href: '/about#team', description: '专业顾问团队' },
        { text: '业务范围', href: '/about#services', description: '我们的服务范围' },
        { text: '服务流程', href: '/about#process', description: '申请到入学的完整流程' },
        { text: '发展历程', href: '/about#timeline', description: '十年发展历程' },
        { text: '服务特色', href: '/about#features', description: '我们的核心优势' }
      ]
    },
    {
      title: '成功案例',
      icon: <SuccessIcon />,
      description: '学生成功申请案例',
      links: [
        { text: '所有案例', href: '/success-cases', description: '查看所有成功案例' },
        { text: '本科申请案例', href: '/success-cases#undergraduate', description: '本科成功申请案例' },
        { text: '硕士申请案例', href: '/success-cases#master', description: '硕士成功申请案例' },
        { text: '博士申请案例', href: '/success-cases#phd', description: '博士成功申请案例' },
        { text: '案例统计', href: '/success-cases#statistics', description: '申请成功率统计' }
      ]
    },
    {
      title: '合作院校',
      icon: <BusinessIcon />,
      description: '官方授权合作院校',
      links: [
        { text: '所有合作院校', href: '/partners', description: '查看所有合作院校' },
        { text: '公立学校授权', href: '/partners#public-schools', description: '马来西亚公立大学' },
        { text: '私立学校授权', href: '/partners#private-schools', description: '马来西亚私立大学' },
        { text: '合作优势', href: '/partners#advantages', description: '我们的合作优势' }
      ]
    },
    {
      title: '联系我们',
      icon: <ContactIcon />,
      description: '获取专业咨询',
      links: [
        { text: '联系方式', href: '/contact', description: '查看联系方式' },
        { text: '中国办公室', href: '/contact#china-office', description: '合肥办公室信息' },
        { text: '马来西亚办公室', href: '/contact#malaysia-office', description: '吉隆坡办公室信息' },
        { text: '在线咨询', href: '/contact#online-consultation', description: '立即在线咨询' }
      ]
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* 英雄区域 */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              网站地图
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
              快速找到您需要的信息，探索希航教育的所有服务
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* 网站地图内容 */}
        <Grid container spacing={4}>
          {siteMapData.map((section, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* 分类标题 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mr: 2
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {section.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {section.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* 链接列表 */}
                  <List dense>
                    {section.links.map((link, linkIndex) => (
                      <ListItem key={linkIndex} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <NavigateNextIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Link
                              href={link.href}
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                fontWeight: 500,
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {link.text}
                            </Link>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                              {link.description}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 快速导航 */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
            快速导航
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {[
              { 
                text: '免费评估', 
                action: () => {
                  setDialogTitle('免费评估咨询');
                  openWeChatQR();
                }, 
                color: 'primary' 
              },
              { 
                text: '在线咨询', 
                action: () => {
                  setDialogTitle('在线咨询');
                  openWeChatQR();
                }, 
                color: 'secondary' 
              },
              { 
                text: '下载指南', 
                action: () => {
                  setDialogTitle('下载申请指南');
                  openWeChatQR();
                }, 
                color: 'success' 
              },
              { 
                text: '查看案例', 
                action: () => window.location.href = '/success-cases', 
                color: 'warning' 
              }
            ].map((item, index) => (
              <Grid item key={index}>
                <Button
                  onClick={item.action}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 25,
                    backgroundColor: `${item.color}.main`,
                    color: 'white',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: `${item.color}.dark`,
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4]
                    }
                  }}
                >
                  {item.text}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <WeChatQRDialog 
        open={openQR} 
        onClose={closeWeChatQR}
        title={dialogTitle}
      />
    </Box>
  );
};

export default Sitemap;
